import {
  discoverSalesCompanies,
  generateSalesReport,
  getLatestSalesReport,
  getSalesJob,
  getSalesReportDiff,
  getSalesReportPdfUrl,
  getSalesReportVersion,
  getSalesReportVersions,
  listSalesCompanies,
  refreshSalesReport,
} from '../api.js';
import { showError, showInfo, showSuccess } from '../utils/toast.js';
import { registerViewCleanup } from '../utils/viewLifecycle.js';

const html = String.raw;

const escapeHtml = (value) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const fieldValue = (entry) => (entry && typeof entry === 'object' && Object.hasOwn(entry, 'value') ? entry.value : entry);
const asDisplay = (entry) => {
  const value = fieldValue(entry);
  if (Array.isArray(value)) return value.join(', ') || 'unknown';
  return value ?? 'unknown';
};

const coverageLabel = (value) => {
  if (value === 'used') return 'aktiv';
  if (value === 'skipped') return 'übersprungen';
  return 'nicht verfügbar';
};

const formatDate = (value) => {
  const parsed = Date.parse(String(value ?? ''));
  if (!Number.isFinite(parsed)) return value ?? '—';
  return new Date(parsed).toLocaleString();
};

const sourceLabel = (source) => {
  if (source === 'saved_report') return 'Gespeicherter Report';
  if (source === 'public_discovery') return 'Public Discovery';
  if (source === 'tavily') return 'Tavily';
  return source || 'Unbekannt';
};

export const renderSalesView = async (container) => {
  const state = {
    searchQuery: '',
    candidates: [],
    savedCompanies: [],
    companyName: '',
    reportId: '',
    latestVersionId: '',
    currentVersion: null,
    versions: [],
    selectedFrom: '',
    selectedTo: '',
    diff: null,
    loading: false,
    discovering: false,
    companiesLoading: false,
    job: null,
  };

  let destroyed = false;
  let pollTimer = null;
  let activeJobId = '';

  const stopPolling = () => {
    if (pollTimer) {
      clearInterval(pollTimer);
      pollTimer = null;
    }
    activeJobId = '';
  };

  const render = () => {
    if (destroyed) return;
    const version = state.currentVersion;
    const companyInfo = version?.report?.sections?.companyInformation ?? {};
    const general = companyInfo.general ?? {};
    const products = companyInfo.products ?? {};
    const production = companyInfo.production ?? {};
    const news = Array.isArray(companyInfo.news) ? companyInfo.news : [];
    const dataCoverage = version?.dataCoverage ?? version?.report?.dataCoverage ?? {};
    const complianceNotes = Array.isArray(version?.complianceNotes)
      ? version.complianceNotes
      : (Array.isArray(version?.report?.complianceNotes) ? version.report.complianceNotes : []);

    container.innerHTML = html`
      <div class="stack" style="gap: 16px;">
        <section class="card stack" style="gap: 12px;">
          <h1 style="margin:0;">Sales Reports</h1>
          <p class="muted" style="margin:0;">Nur für Admins. Erst Firma recherchieren, dann Bericht laden/erzeugen und versionieren.</p>
          <div class="row" style="gap: 8px; flex-wrap: wrap;">
            <input
              type="text"
              id="sales-company-input"
              placeholder="Firmenname eingeben und Enter drücken"
              value="${escapeHtml(state.searchQuery)}"
              style="min-width: 300px; flex:1;"
            />
            <button type="button" id="sales-discover-btn"${state.loading || state.discovering ? ' disabled' : ''}>Recherche</button>
          </div>
          ${state.discovering ? '<p class="muted small">Recherche läuft…</p>' : ''}
          ${state.job ? `<p class="muted small" id="sales-job-status">Job: ${escapeHtml(state.job.status)} (${Number(state.job.progress ?? 0)}%)</p>` : ''}
        </section>

        ${(state.searchQuery || state.candidates.length) ? html`
          <section class="card stack" style="gap: 12px;">
            <div class="row" style="justify-content: space-between; gap: 8px; flex-wrap: wrap;">
              <strong>Recherche-Ergebnisse</strong>
              <span class="muted small">Suche: ${escapeHtml(state.searchQuery || '—')}</span>
            </div>
            ${state.candidates.length ? html`
              <div class="stack tight">
                ${state.candidates.map((candidate, index) => html`
                  <div class="row" style="justify-content: space-between; gap: 8px; align-items:center; flex-wrap: wrap;">
                    <div class="stack tight">
                      <strong>${escapeHtml(candidate.displayName || candidate.canonicalName || 'Unbekannt')}</strong>
                      <span class="muted small">
                        Quelle: ${escapeHtml(sourceLabel(candidate.source))}
                        ${candidate.domain ? ` • ${escapeHtml(candidate.domain)}` : ''}
                        ${Number.isFinite(Number(candidate.confidence)) ? ` • Score ${escapeHtml(Number(candidate.confidence).toFixed(2))}` : ''}
                      </span>
                    </div>
                    <button
                      type="button"
                      class="ghost-button"
                      data-select-candidate="${escapeHtml(String(index))}"
                      ${state.loading ? 'disabled' : ''}
                    >
                      ${candidate.source === 'saved_report' ? 'Öffnen' : 'Auswählen'}
                    </button>
                  </div>
                `).join('')}
              </div>
            ` : '<p class="muted small">Keine Treffer gefunden.</p>'}

            <div class="row" style="justify-content: flex-end;">
              <button type="button" id="sales-manual-btn"${state.loading || !state.searchQuery ? ' disabled' : ''}>
                „${escapeHtml(state.searchQuery || '')}“ manuell verwenden
              </button>
            </div>
          </section>
        ` : ''}

        <section class="card stack" style="gap: 12px;">
          <div class="row" style="justify-content: space-between; gap: 8px; flex-wrap: wrap;">
            <strong>Meine Firmenberichte</strong>
            <button type="button" class="ghost-button small" id="sales-reload-companies-btn"${state.companiesLoading ? ' disabled' : ''}>Neu laden</button>
          </div>
          ${state.savedCompanies.length
            ? `<div class="stack tight">${state.savedCompanies.map((entry) => `
                <div class="row" style="justify-content: space-between; gap: 8px; align-items:center; flex-wrap: wrap;">
                  <div class="stack tight">
                    <strong>${escapeHtml(entry.companyName)}</strong>
                    <span class="muted small">Zuletzt: ${escapeHtml(formatDate(entry.latestCreatedAt))} • Versionen: ${Number(entry.versionCount ?? 0)}</span>
                  </div>
                  <button type="button" class="ghost-button small" data-open-company="${escapeHtml(entry.companyName)}"${state.loading ? ' disabled' : ''}>Öffnen</button>
                </div>
              `).join('')}</div>`
            : '<p class="muted small">Noch keine gespeicherten Firmenberichte.</p>'
          }
        </section>

        ${version ? html`
          <section class="card stack" style="gap: 12px;">
            <div class="row" style="justify-content: space-between; gap: 12px; flex-wrap: wrap;">
              <div class="stack tight">
                <h2 style="margin:0;">${escapeHtml(state.companyName)}</h2>
                <p class="muted small" style="margin:0;">Version ${escapeHtml(version.versionId)} • ${escapeHtml(formatDate(version.createdAt))}</p>
              </div>
              <div class="row" style="gap:8px;">
                <button type="button" id="sales-refresh-btn"${state.loading ? ' disabled' : ''}>Aktualisieren</button>
                <button type="button" class="ghost-button" id="sales-pdf-btn">PDF herunterladen</button>
              </div>
            </div>

            <div class="row" style="gap: 12px; flex-wrap: wrap;">
              <span class="pill">LinkedIn: ${escapeHtml(coverageLabel(dataCoverage.linkedin))}</span>
              <span class="pill">Public Web: ${escapeHtml(coverageLabel(dataCoverage.publicWeb))}</span>
              <span class="pill">News: ${escapeHtml(coverageLabel(dataCoverage.news))}</span>
              <span class="pill">Web: ${escapeHtml(coverageLabel(dataCoverage.web))}</span>
            </div>

            ${complianceNotes.length ? html`
              <div class="stack tight">
                <strong>Compliance Notes</strong>
                <ul style="margin:0; padding-left: 18px;">
                  ${complianceNotes.map((note) => `<li>${escapeHtml(note)}</li>`).join('')}
                </ul>
              </div>
            ` : ''}

            <div class="grid" style="display:grid; gap:12px; grid-template-columns: repeat(auto-fit,minmax(280px,1fr));">
              <div class="card" style="padding:12px;">
                <strong>Allgemein</strong>
                <p class="muted small">Webseite: ${escapeHtml(asDisplay(general.website))}</p>
                <p class="muted small">Branche: ${escapeHtml(asDisplay(general.industry))}</p>
                <p class="muted small">Mitarbeiter: ${escapeHtml(asDisplay(general.employeeCount))}</p>
                <p class="muted small">Adresse: ${escapeHtml(asDisplay(general.address))}</p>
              </div>
              <div class="card" style="padding:12px;">
                <strong>Produkte & Kunden</strong>
                <p class="muted small">Produkte: ${escapeHtml(asDisplay(products.offerings))}</p>
                <p class="muted small">Kunden: ${escapeHtml(asDisplay(products.customerSegments))}</p>
              </div>
              <div class="card" style="padding:12px;">
                <strong>Produktion</strong>
                <p class="muted small">Maschinen: ${escapeHtml(asDisplay(production.machines))}</p>
                <p class="muted small">Produktionsleiter: ${escapeHtml(asDisplay(production.productionLeader))}</p>
              </div>
            </div>

            <div class="stack tight">
              <strong>News (letzte 12 Monate)</strong>
              ${news.length
                ? `<ul style="margin:0; padding-left: 18px;">${news.map((entry) =>
                    `<li><a href="${escapeHtml(entry.url || '#')}" target="_blank" rel="noopener">${escapeHtml(entry.title || 'News')}</a> <span class="muted small">(${escapeHtml(entry.publishedAt || 'n/a')})</span></li>`).join('')}</ul>`
                : '<p class="muted small">Keine News verfügbar.</p>'
              }
            </div>
          </section>

          <section class="card stack" style="gap: 12px;">
            <div class="row" style="justify-content: space-between; gap: 12px; flex-wrap: wrap;">
              <strong>Versionen</strong>
              <span class="muted small">Report ID: ${escapeHtml(state.reportId)}</span>
            </div>

            <div class="row" style="gap: 8px; flex-wrap: wrap;">
              <select id="sales-from-version" style="min-width:180px;">
                <option value="">Vergleich: Von Version</option>
                ${state.versions.map((entry) => `<option value="${escapeHtml(entry.versionId)}"${state.selectedFrom === entry.versionId ? ' selected' : ''}>${escapeHtml(entry.versionId)} (${escapeHtml(formatDate(entry.createdAt))})</option>`).join('')}
              </select>
              <select id="sales-to-version" style="min-width:180px;">
                <option value="">Vergleich: Zu Version</option>
                ${state.versions.map((entry) => `<option value="${escapeHtml(entry.versionId)}"${state.selectedTo === entry.versionId ? ' selected' : ''}>${escapeHtml(entry.versionId)} (${escapeHtml(formatDate(entry.createdAt))})</option>`).join('')}
              </select>
              <button type="button" class="ghost-button" id="sales-diff-btn"${state.loading ? ' disabled' : ''}>Diff laden</button>
            </div>

            <div class="stack tight">
              ${state.versions.map((entry) => `
                <div class="row" style="justify-content: space-between; gap: 8px; align-items:center;">
                  <span>${escapeHtml(entry.versionId)} • ${escapeHtml(formatDate(entry.createdAt))}</span>
                  <button type="button" class="ghost-button small" data-open-version="${escapeHtml(entry.versionId)}">Öffnen</button>
                </div>
              `).join('')}
            </div>

            ${state.diff ? html`
              <div class="stack tight">
                <strong>Diff ${escapeHtml(state.diff.fromVersionId)} → ${escapeHtml(state.diff.toVersionId)}</strong>
                <p class="muted small">Änderungen: ${Number(state.diff.changeCount ?? 0)}</p>
                ${Array.isArray(state.diff.changes) && state.diff.changes.length
                  ? `<ul style="margin:0; padding-left:18px;">${state.diff.changes.slice(0, 40).map((change) =>
                      `<li><code>${escapeHtml(change.path)}</code> (${escapeHtml(change.changeType)})</li>`).join('')}</ul>`
                  : '<p class="muted small">Keine Änderungen.</p>'}
              </div>
            ` : ''}
          </section>
        ` : ''}
      </div>
    `;

    const input = container.querySelector('#sales-company-input');
    const discoverButton = container.querySelector('#sales-discover-btn');
    const manualButton = container.querySelector('#sales-manual-btn');
    const reloadCompaniesButton = container.querySelector('#sales-reload-companies-btn');
    const refreshButton = container.querySelector('#sales-refresh-btn');
    const pdfButton = container.querySelector('#sales-pdf-btn');
    const diffButton = container.querySelector('#sales-diff-btn');
    const fromSelect = container.querySelector('#sales-from-version');
    const toSelect = container.querySelector('#sales-to-version');

    const triggerDiscover = async () => {
      const query = String(input?.value ?? '').trim();
      if (!query) {
        showInfo('Bitte Firmenname eingeben.');
        return;
      }
      state.searchQuery = query;
      state.discovering = true;
      state.candidates = [];
      render();
      try {
        const payload = await discoverSalesCompanies({ query, limit: 12 });
        state.candidates = Array.isArray(payload?.candidates) ? payload.candidates : [];
        if (!state.candidates.length) {
          showInfo('Keine Treffer aus Recherche. Du kannst den Namen manuell verwenden.');
        }
      } catch (error) {
        console.error('Failed to discover sales companies', error);
        state.candidates = [];
        showError(error?.userMessage ?? error?.message ?? 'Recherche fehlgeschlagen.');
      } finally {
        state.discovering = false;
        render();
      }
    };

    const loadLatestAndVersions = async (companyName) => {
      const latest = await getLatestSalesReport({ companyName });
      state.reportId = latest.reportId;
      state.companyName = latest.companyName || companyName;
      state.latestVersionId = latest.latestVersionId;
      state.currentVersion = latest.version;
      state.diff = null;
      const versionsPayload = await getSalesReportVersions(state.reportId);
      state.versions = Array.isArray(versionsPayload?.versions) ? versionsPayload.versions : [];
    };

    const openOrCreateForCompany = async (companyName, successMessage = 'Neueste Version geladen.') => {
      const normalizedCompany = String(companyName ?? '').trim();
      if (!normalizedCompany) return;
      state.companyName = normalizedCompany;
      state.loading = true;
      render();
      try {
        await loadLatestAndVersions(normalizedCompany);
        showSuccess(successMessage);
      } catch (error) {
        if (error?.status === 404) {
          const created = await generateSalesReport({ companyName: normalizedCompany });
          state.reportId = created.reportId;
          if (created?.jobId) {
            state.job = { status: created.status ?? 'queued', progress: 0 };
            showInfo('Neuer Bericht wird im Hintergrund erstellt.');
            await startPolling(created.jobId);
          } else {
            await finishWithLatestVersion();
            showSuccess('Neueste Version geladen.');
          }
        } else {
          console.error('Failed to load or create sales report', error);
          showError(error?.userMessage ?? error?.message ?? 'Report konnte nicht geladen werden.');
        }
      } finally {
        state.loading = false;
        render();
      }
    };

    const selectCandidate = async (candidateIndex) => {
      const candidate = state.candidates[Number(candidateIndex)];
      if (!candidate) return;
      const targetCompany = String(candidate.canonicalName || candidate.displayName || '').trim();
      if (!targetCompany) {
        showInfo('Ungültiger Firmenkandidat.');
        return;
      }
      await openOrCreateForCompany(targetCompany, candidate.source === 'saved_report' ? 'Bestehenden Report geöffnet.' : 'Report für Auswahl geladen.');
    };

    const useManualSearchQuery = async () => {
      const query = String(input?.value ?? state.searchQuery ?? '').trim();
      if (!query) {
        showInfo('Bitte Firmenname eingeben.');
        return;
      }
      await openOrCreateForCompany(query, 'Manuelle Firma verarbeitet.');
    };

    const loadVersion = async (targetVersionId) => {
      if (!state.reportId || !targetVersionId) return;
      state.loading = true;
      render();
      try {
        state.currentVersion = await getSalesReportVersion(state.reportId, targetVersionId);
        state.latestVersionId = state.currentVersion.versionId;
      } catch (error) {
        console.error('Failed to load version', error);
        showError(error?.userMessage ?? error?.message ?? 'Version konnte nicht geladen werden.');
      } finally {
        state.loading = false;
        render();
      }
    };

    const triggerRefresh = async () => {
      if (!state.reportId) return;
      state.loading = true;
      render();
      try {
        const response = await refreshSalesReport(state.reportId);
        state.job = { status: response.status ?? 'queued', progress: 0 };
        showInfo('Aktualisierung gestartet.');
        await startPolling(response.jobId);
      } catch (error) {
        console.error('Failed to refresh sales report', error);
        showError(error?.userMessage ?? error?.message ?? 'Aktualisierung fehlgeschlagen.');
      } finally {
        state.loading = false;
        render();
      }
    };

    const downloadPdf = async () => {
      if (!state.reportId || !state.currentVersion?.versionId) return;
      try {
        const response = await getSalesReportPdfUrl(state.reportId, state.currentVersion.versionId);
        if (!response?.url) {
          throw new Error('PDF-URL nicht verfügbar.');
        }
        const link = document.createElement('a');
        link.href = response.url;
        link.download = '';
        link.rel = 'noopener';
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        link.remove();
      } catch (error) {
        console.error('Failed to download sales PDF', error);
        showError(error?.userMessage ?? error?.message ?? 'PDF konnte nicht heruntergeladen werden.');
      }
    };

    const loadDiff = async () => {
      if (!state.reportId || !state.selectedFrom || !state.selectedTo) {
        showInfo('Bitte beide Versionen für den Vergleich wählen.');
        return;
      }
      state.loading = true;
      render();
      try {
        state.diff = await getSalesReportDiff({
          reportId: state.reportId,
          from: state.selectedFrom,
          to: state.selectedTo,
        });
      } catch (error) {
        console.error('Failed to load sales diff', error);
        showError(error?.userMessage ?? error?.message ?? 'Diff konnte nicht geladen werden.');
      } finally {
        state.loading = false;
        render();
      }
    };

    discoverButton?.addEventListener('click', () => {
      void triggerDiscover();
    });
    input?.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        void triggerDiscover();
      }
    });
    manualButton?.addEventListener('click', () => {
      void useManualSearchQuery();
    });
    reloadCompaniesButton?.addEventListener('click', () => {
      void loadSavedCompanies({ silent: false });
    });
    refreshButton?.addEventListener('click', () => {
      void triggerRefresh();
    });
    pdfButton?.addEventListener('click', () => {
      void downloadPdf();
    });
    diffButton?.addEventListener('click', () => {
      void loadDiff();
    });
    fromSelect?.addEventListener('change', () => {
      state.selectedFrom = fromSelect.value;
    });
    toSelect?.addEventListener('change', () => {
      state.selectedTo = toSelect.value;
    });

    container.querySelectorAll('[data-select-candidate]').forEach((button) => {
      button.addEventListener('click', () => {
        const candidateIndex = button.getAttribute('data-select-candidate');
        if (candidateIndex !== null) {
          void selectCandidate(candidateIndex);
        }
      });
    });

    container.querySelectorAll('[data-open-company]').forEach((button) => {
      button.addEventListener('click', () => {
        const company = button.getAttribute('data-open-company');
        if (company) {
          void openOrCreateForCompany(company, 'Bestehenden Report geöffnet.');
        }
      });
    });

    container.querySelectorAll('[data-open-version]').forEach((button) => {
      button.addEventListener('click', () => {
        const targetVersionId = button.getAttribute('data-open-version');
        if (targetVersionId) {
          void loadVersion(targetVersionId);
        }
      });
    });
  };

  const loadSavedCompanies = async ({ silent = true } = {}) => {
    if (destroyed) return;
    state.companiesLoading = true;
    render();
    try {
      const payload = await listSalesCompanies({ limit: 50 });
      state.savedCompanies = Array.isArray(payload?.companies) ? payload.companies : [];
    } catch (error) {
      console.error('Failed to load saved sales companies', error);
      if (!silent) {
        showError(error?.userMessage ?? error?.message ?? 'Firmeliste konnte nicht geladen werden.');
      }
    } finally {
      state.companiesLoading = false;
      render();
    }
  };

  const finishWithLatestVersion = async () => {
    const latest = await getLatestSalesReport({ companyName: state.companyName });
    state.reportId = latest.reportId;
    state.latestVersionId = latest.latestVersionId;
    state.currentVersion = latest.version;
    const versionsPayload = await getSalesReportVersions(state.reportId);
    state.versions = Array.isArray(versionsPayload?.versions) ? versionsPayload.versions : [];
    state.job = null;
    state.diff = null;
    const companiesPayload = await listSalesCompanies({ limit: 50 });
    state.savedCompanies = Array.isArray(companiesPayload?.companies) ? companiesPayload.companies : [];
  };

  const startPolling = async (jobId) => {
    if (!jobId) return;
    stopPolling();
    activeJobId = jobId;

    const check = async () => {
      if (destroyed || !activeJobId) return;
      try {
        const payload = await getSalesJob(activeJobId);
        state.job = payload?.job ?? null;
        render();
        const status = String(state.job?.status ?? '').toLowerCase();
        if (status === 'succeeded') {
          stopPolling();
          await finishWithLatestVersion();
          showSuccess('Report-Version erfolgreich erstellt.');
          render();
        } else if (status === 'failed') {
          stopPolling();
          showError(state.job?.error || 'Report-Erstellung fehlgeschlagen.');
        }
      } catch (error) {
        stopPolling();
        console.error('Failed to poll sales job', error);
        showError(error?.userMessage ?? error?.message ?? 'Job-Status konnte nicht geladen werden.');
      }
    };

    await check();
    if (activeJobId) {
      pollTimer = window.setInterval(() => {
        void check();
      }, 3000);
    }
  };

  registerViewCleanup(() => {
    destroyed = true;
    stopPolling();
  });

  render();
  void loadSavedCompanies();
};

